/**
 * Roadmap export — a print-ready page the browser turns into a PDF
 * ("Save as PDF" in the print dialog). No dependency, no server round trip,
 * and the output is real text, so it stays accessible and searchable.
 */
import { ACTION_LABEL, type Roadmap } from "@/features/roadmap/roadmaps";
import { BAND_LABEL } from "@/features/assessment-v2/band";

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );
}

export function exportRoadmapPdf(roadmap: Roadmap, learnerName?: string | null): boolean {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) return false;

  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const steps = roadmap.milestones
    .map(
      (m) => `
      <section class="step">
        <p class="phase">${esc(m.phase)}</p>
        <h2>${esc(m.title)}</h2>
        <p class="why">${esc(m.why)}</p>
        <ul>
          ${m.actions
            .map(
              (a) =>
                `<li><span class="kind">${esc(ACTION_LABEL[a.kind])}</span> ${esc(a.label)} <span class="mins">· ${a.minutes} min</span></li>`,
            )
            .join("")}
        </ul>
      </section>`,
    )
    .join("");

  win.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8" />
<title>${esc(roadmap.title)} | WeKIT Career Clarity 360</title>
<style>
  @page { margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: Inter, system-ui, -apple-system, sans-serif; color: #180205; margin: 0; line-height: 1.5; }
  header { border-bottom: 3px solid #00665B; padding-bottom: 14px; margin-bottom: 22px; }
  .eyebrow { text-transform: uppercase; letter-spacing: .14em; font-size: 10px; color: #00665B; margin: 0 0 6px; font-weight: 600; }
  h1 { font-size: 26px; margin: 0 0 6px; }
  .meta { font-size: 12px; color: #555; margin: 0; }
  .intro { font-size: 13px; margin: 0 0 22px; color: #333; }
  .step { page-break-inside: avoid; border-left: 3px solid #D9D9D9; padding: 0 0 2px 14px; margin-bottom: 20px; }
  .phase { font-size: 10px; text-transform: uppercase; letter-spacing: .12em; color: #00665B; margin: 0 0 4px; font-weight: 600; }
  h2 { font-size: 16px; margin: 0 0 4px; }
  .why { font-size: 12px; color: #444; margin: 0 0 8px; }
  ul { margin: 0; padding-left: 18px; }
  li { font-size: 12.5px; margin-bottom: 5px; }
  .kind { font-weight: 600; color: #00665B; }
  .mins { color: #777; }
  footer { margin-top: 26px; border-top: 1px solid #D9D9D9; padding-top: 10px; font-size: 10.5px; color: #666; }
</style></head><body>
<header>
  <p class="eyebrow">WeKIT™ Career Clarity 360 · ${esc(roadmap.kind === "journey" ? "Journey roadmap" : "Learning roadmap")}</p>
  <h1>${esc(roadmap.title)}</h1>
  <p class="meta">${learnerName ? esc(learnerName) + " · " : ""}Age band ${esc(BAND_LABEL[roadmap.band])} · Prepared ${esc(today)}</p>
</header>
<p class="intro">${esc(roadmap.intro)}</p>
${steps}
<footer>Composed from your Career DNA™ archetype and age band. Your scores, matches and evidence stay in your report — this is the doing part.</footer>
</body></html>`);
  win.document.close();
  win.focus();
  // Give the print stylesheet a tick to apply before the dialog opens.
  window.setTimeout(() => win.print(), 250);
  return true;
}
